import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { shopApi } from '../../api/shop';

// 异步Action
export const fetchShopList = createAsyncThunk(
  'shop/fetchShopList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await shopApi.getShopList(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取店铺列表失败');
    }
  }
);

export const fetchShopDetail = createAsyncThunk(
  'shop/fetchShopDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await shopApi.getShopDetail(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取店铺详情失败');
    }
  }
);

export const createShop = createAsyncThunk(
  'shop/createShop',
  async (data, { rejectWithValue }) => {
    try {
      const response = await shopApi.createShop(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '创建店铺失败');
    }
  }
);

export const updateShop = createAsyncThunk(
  'shop/updateShop',
  async (data, { rejectWithValue }) => {
    try {
      const response = await shopApi.updateShop(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '更新店铺失败');
    }
  }
);

export const deleteShop = createAsyncThunk(
  'shop/deleteShop',
  async (id, { rejectWithValue }) => {
    try {
      const response = await shopApi.deleteShop(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '删除店铺失败');
    }
  }
);

export const updateShopStatus = createAsyncThunk(
  'shop/updateShopStatus',
  async (data, { rejectWithValue }) => {
    try {
      const response = await shopApi.updateShopStatus(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '更新店铺状态失败');
    }
  }
);

// 初始状态
const initialState = {
  shopList: [],
  shopDetail: null,
  shopPagination: {
    current: 1,
    pageSize: 10,
    total: 0
  },
  loading: false,
  detailLoading: false,
  error: null
};

// 创建Slice
const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    setShopPagination: (state, action) => {
      state.shopPagination = { ...state.shopPagination, ...action.payload };
    },
    clearShopDetail: (state) => {
      state.shopDetail = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取店铺列表
      .addCase(fetchShopList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShopList.fulfilled, (state, action) => {
        state.loading = false;
        state.shopList = action.payload.records || [];
        state.shopPagination.total = action.payload.total || 0;
      })
      .addCase(fetchShopList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || '获取店铺列表失败';
      })
      // 获取店铺详情
      .addCase(fetchShopDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchShopDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.shopDetail = action.payload;
      })
      .addCase(fetchShopDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload || '获取店铺详情失败';
      })
      // 创建店铺
      .addCase(createShop.fulfilled, (state, action) => {
        state.shopList.unshift(action.payload);
        state.shopPagination.total += 1;
      })
      // 更新店铺
      .addCase(updateShop.fulfilled, (state, action) => {
        const index = state.shopList.findIndex(shop => shop.id === action.payload.id);
        if (index !== -1) {
          state.shopList[index] = action.payload;
        }
        if (state.shopDetail && state.shopDetail.id === action.payload.id) {
          state.shopDetail = action.payload;
        }
      })
      // 删除店铺
      .addCase(deleteShop.fulfilled, (state, action) => {
        state.shopList = state.shopList.filter(shop => shop.id !== action.payload.id);
        state.shopPagination.total -= 1;
        if (state.shopDetail && state.shopDetail.id === action.payload.id) {
          state.shopDetail = null;
        }
      })
      // 更新店铺状态
      .addCase(updateShopStatus.fulfilled, (state, action) => {
        const index = state.shopList.findIndex(shop => shop.id === action.payload.id);
        if (index !== -1) {
          state.shopList[index] = { ...state.shopList[index], ...action.payload };
        }
        if (state.shopDetail && state.shopDetail.id === action.payload.id) {
          state.shopDetail = { ...state.shopDetail, ...action.payload };
        }
      });
  }
});

export const { setShopPagination, clearShopDetail, clearError } = shopSlice.actions;
export default shopSlice.reducer; 